import AudioRecording from '@src/components/AudioRecording';
import { AUDIO_RECORDING_MAX_WIDTH } from '@src/components/AudioRecording/AudioRecording';
import { MediaType, supportsVoiceMessage } from '@src/model/frontendmodel';
import { setAudioRecording } from '@src/reducers/audioRecording';
import { selectAttachmentQueue } from '@src/reducers/chatMessageQueue';
import { isComposerToolbarDisabled } from '@src/selectors/agentConsole';
import { audioRecordingSelector } from '@src/selectors/audioRecording';
import { selectHasOngoingCall } from '@src/selectors/calls';
import {
  activeChatSelector,
  activeWrittenMessageSelector,
} from '@src/selectors/chats';
import { RootState } from '@src/store/reduxstore';
import mediaUtils from '@src/utils/mediaUtils';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import SendIcon from './SendIcon';

interface ISendButton {
  sendAudioRecording: (buffer: BlobPart[]) => void;
  handleSendMessage: () => void;
}

export const SendButton: React.FC<ISendButton> = ({
  sendAudioRecording,
  handleSendMessage,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const { isRecording } = useSelector(audioRecordingSelector);
  const activeChat = useSelector(activeChatSelector);
  const attachmentQueue = useSelector(selectAttachmentQueue(activeChat?.id));
  const activeWrittenMessage = useSelector(activeWrittenMessageSelector);

  const hasCallOngoing = useSelector(selectHasOngoingCall);
  const composerToolBarDisabled = useSelector(isComposerToolbarDisabled);
  const isAudioDisabled = composerToolBarDisabled || hasCallOngoing;

  const { isAudioRecordingEnabled } = useSelector(
    (state: RootState) => state.generalProperties
  );

  const queueFiles = useSelector(
    (state: RootState) => state.quickMessages.queueFiles
  );

  const msgProvider = activeChat?.provider;
  const showAudioRecordButton =
    attachmentQueue.length === 0 &&
    !activeWrittenMessage?.length &&
    msgProvider &&
    supportsVoiceMessage(msgProvider) &&
    isAudioRecordingEnabled &&
    mediaUtils.isMediaTypeSupported(MediaType.AUDIO, msgProvider) &&
    queueFiles.length === 0;

  const view = showAudioRecordButton ? (
    <AudioRecording
      onRecordingStopped={sendAudioRecording}
      setIsRecording={(value: boolean) => dispatch(setAudioRecording(value))}
      isRecording={isRecording}
      disabled={isAudioDisabled}
      cancelLabel={formatMessage({
        id: 'AgentToolbar.composer.audioRecording.cancel.label',
      })}
    />
  ) : (
    <SendIcon
      handleSendMessage={handleSendMessage}
      disabled={composerToolBarDisabled}
    />
  );

  return (
    <div
      style={{
        width: isRecording ? `${AUDIO_RECORDING_MAX_WIDTH}px` : '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {view}
    </div>
  );
};

export default SendButton;
